import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  BanknotesIcon,
  BellIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { useFormatting } from '../hooks/useFormatting';

interface IntegrationStatus {
  accounting: {
    enabled: boolean;
    description: string;
  };
  notifications: {
    enabled: boolean;
    description: string;
  };
  suppliers: {
    enabled: boolean;
    description: string;
  };
  systemSettings: {
    enabled: boolean;
    description: string;
  };
  features: string[];
}

interface SupplierIntegrationData {
  supplier: any;
  totalSupplies: number;
  totalValue: number;
  lowStockItems: number;
  supplies: any[];
}

const SupplyIntegrationPanel: React.FC = () => {
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus | null>(null);
  const [supplierData, setSupplierData] = useState<SupplierIntegrationData | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const { formatCurrency, formatDateTimeFull } = useFormatting();

  useEffect(() => {
    loadIntegrationStatus();
    loadSuppliers();
  }, []);

  const loadIntegrationStatus = async () => {
    try {
      const response = await apiClient.get<IntegrationStatus>('supplies/integration-status');
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error('Failed to load integration status:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await apiClient.get('suppliers');
      // Handle both paginated and direct array responses
      const suppliersData = (response.data as any)?.suppliers || response.data || [];
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  };

  const loadSupplierIntegration = async (supplierId: string) => {
    if (!supplierId) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get<SupplierIntegrationData>(
        `supplies/supplier-integration/${supplierId}`
      );
      setSupplierData(response.data);
    } catch (error) {
      console.error('Failed to load supplier integration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncWithSettings = async () => {
    setSyncing(true);
    try {
      await apiClient.post('supplies/sync-settings');
      setLastSync(new Date());
    } catch (error) {
      console.error('Failed to sync with settings:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (enabled: boolean) => {
    return enabled ? (
      <CheckCircleIcon className='h-5 w-5 text-green-500' />
    ) : (
      <ExclamationTriangleIcon className='h-5 w-5 text-red-500' />
    );
  };

  if (!integrationStatus) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='bg-white shadow rounded-lg p-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-xl font-semibold text-slate-900'>
              Supplies Module Integration Status
            </h2>
            <p className='text-sm text-slate-600 mt-1'>
              Complete end-to-end integration with all system modules
            </p>
          </div>
          <button
            onClick={syncWithSettings}
            disabled={syncing}
            className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50'
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Settings'}
          </button>
        </div>
        {lastSync && (
          <p className='text-xs text-slate-500 mt-2'>
            Last sync: {formatDateTimeFull(lastSync)}
          </p>
        )}
      </div>

      {/* Integration Status Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        {/* Accounting Integration */}
        <div className='bg-white shadow rounded-lg p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <BanknotesIcon className='h-8 w-8 text-green-600' />
            </div>
            <div className='ml-4 flex-1'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium text-slate-900'>Accounting</h3>
                {getStatusIcon(integrationStatus.accounting.enabled)}
              </div>
              <p className='text-sm text-slate-600 mt-1'>
                {integrationStatus.accounting.description}
              </p>
            </div>
          </div>
        </div>

        {/* Notifications Integration */}
        <div className='bg-white shadow rounded-lg p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <BellIcon className='h-8 w-8 text-cyan-600' />
            </div>
            <div className='ml-4 flex-1'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium text-slate-900'>Notifications</h3>
                {getStatusIcon(integrationStatus.notifications.enabled)}
              </div>
              <p className='text-sm text-slate-600 mt-1'>
                {integrationStatus.notifications.description}
              </p>
            </div>
          </div>
        </div>

        {/* Suppliers Integration */}
        <div className='bg-white shadow rounded-lg p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <BuildingStorefrontIcon className='h-8 w-8 text-purple-600' />
            </div>
            <div className='ml-4 flex-1'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium text-slate-900'>Suppliers</h3>
                {getStatusIcon(integrationStatus.suppliers.enabled)}
              </div>
              <p className='text-sm text-slate-600 mt-1'>
                {integrationStatus.suppliers.description}
              </p>
            </div>
          </div>
        </div>

        {/* System Settings Integration */}
        <div className='bg-white shadow rounded-lg p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <CogIcon className='h-8 w-8 text-orange-600' />
            </div>
            <div className='ml-4 flex-1'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium text-slate-900'>Settings</h3>
                {getStatusIcon(integrationStatus.systemSettings.enabled)}
              </div>
              <p className='text-sm text-slate-600 mt-1'>
                {integrationStatus.systemSettings.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features List */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='text-lg font-medium text-slate-900 mb-4'>
          Integrated Features
        </h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {integrationStatus.features.map((feature, index) => (
            <div key={index} className='flex items-center'>
              <CheckCircleIcon className='h-5 w-5 text-green-500 mr-3' />
              <span className='text-sm text-slate-700'>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Supplier Integration Demo */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='text-lg font-medium text-slate-900 mb-4'>
          Supplier Integration Demo
        </h3>
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-slate-700'>
              Select Supplier
            </label>
            <select
              value={selectedSupplierId}
              onChange={(e) => {
                setSelectedSupplierId(e.target.value);
                if (e.target.value) {
                  loadSupplierIntegration(e.target.value);
                }
              }}
              className='mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500'
            >
              <option value=''>Choose a supplier...</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name} ({supplier.code})
                </option>
              ))}
            </select>
          </div>

          {loading && (
            <div className='flex items-center justify-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            </div>
          )}

          {supplierData && !loading && (
            <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mt-6'>
              <div className='bg-cyan-50 p-4 rounded-lg'>
                <div className='text-2xl font-bold text-cyan-600'>
                  {supplierData.totalSupplies}
                </div>
                <div className='text-sm text-cyan-800'>Total Supplies</div>
              </div>
              <div className='bg-green-50 p-4 rounded-lg'>
                <div className='text-2xl font-bold text-green-600'>
                  {formatCurrency(supplierData.totalValue)}
                </div>
                <div className='text-sm text-green-800'>Total Value</div>
              </div>
              <div className='bg-yellow-50 p-4 rounded-lg'>
                <div className='text-2xl font-bold text-yellow-600'>
                  {supplierData.lowStockItems}
                </div>
                <div className='text-sm text-yellow-800'>Low Stock Items</div>
              </div>
              <div className='bg-purple-50 p-4 rounded-lg'>
                <div className='text-2xl font-bold text-purple-600'>
                  {supplierData.supplier.name}
                </div>
                <div className='text-sm text-purple-800'>Supplier Name</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Integration Info */}
      <div className='bg-cyan-50 border border-blue-200 rounded-lg p-4'>
        <div className='flex'>
          <InformationCircleIcon className='h-5 w-5 text-blue-400 mt-0.5' />
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-cyan-800'>
              Complete Integration Achieved
            </h3>
            <div className='mt-2 text-sm text-cyan-700'>
              <p>
                The supplies module is now fully integrated with all system components:
              </p>
              <ul className='list-disc list-inside mt-2 space-y-1'>
                <li>Automatic transaction creation for purchases and adjustments</li>
                <li>Real-time notifications for stock levels and expiry dates</li>
                <li>Seamless supplier data integration and tracking</li>
                <li>Settings-driven automation and thresholds</li>
                <li>End-to-end audit trail and reporting</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplyIntegrationPanel; 